import type { Range } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import { MINUTE_HEIGHT_PX } from 'constants/hour';
import { TIME_ENTRY_WIDTH_PX, TIMESPAN_SPACING_PX, TIMESPAN_WIDTH_PX } from 'constants/timeline';
import { SHOW_HOUR_EDITOR_ATTACHMENTS } from 'constants/user';
import isUrl from 'is-url';
import { max, range } from 'lodash-es';
import { DateTime } from 'luxon';
import { TimelineStyleTypes } from 'redux/modules/timeline.types';
import type { UserProperties } from 'redux/modules/user.types';
import type {
  DisplayRules,
  Entry,
  EntryAttributes,
  ExtractTimelineMetaFromEntries,
  Memory,
  MetaHourList,
  SuggestedEntry,
} from 'types/timeline';
import type { UserTimeFormat } from 'types/user';
import { fromISO } from 'utils/luxon-helpers';
import { percentOf } from 'utils/numbers';
import { formatTime, isSameDay } from './date-helper';
import { slugify } from './text-helper';

interface GetTimelineScrollToPx {
  fromDateTime: DateTime<true>;
  hourDraftFrom: string | null;
  pixelsPerMinute: number;
  defaultHoursFromStart?: number;
}
export function getTimelineScrollToPx({
  fromDateTime,
  hourDraftFrom,
  pixelsPerMinute,
  defaultHoursFromStart = 7,
}: GetTimelineScrollToPx) {
  // Hour Draft with timestamps? Scroll to 1 hour before hour.from starts
  if (hourDraftFrom) {
    const fromStart = fromISO(hourDraftFrom).diff(fromDateTime).as('minutes');
    const adjustedStart = fromStart >= 60 ? fromStart - 60 : fromStart;
    return adjustedStart * pixelsPerMinute;
  }

  // Is today? Scroll to now - 2 hours.
  if (DateTime.now().hasSame(fromDateTime, 'day')) {
    const fromStart = DateTime.now().diff(fromDateTime).as('minutes');
    const adjustedStart = fromStart >= 120 ? fromStart - 120 : fromStart;
    return adjustedStart * pixelsPerMinute;
  }

  // Scroll to ~7am by default
  return defaultHoursFromStart * 60 * pixelsPerMinute;
}

export function createTimelineMetadata(
  entries: Array<Entry>,
  options: {
    date: string | null;
    pixelsPerMinute: number; // minuteHeightInPx,
    timeFormat: UserTimeFormat;
    hourDraftFrom: string | null;
  } = {
    date: null,
    pixelsPerMinute: MINUTE_HEIGHT_PX,
    timeFormat: 24,
    hourDraftFrom: null,
  }
): ExtractTimelineMetaFromEntries {
  const { pixelsPerMinute, timeFormat, hourDraftFrom } = options;
  const entryStartHours = entries.map(entry => {
    if (!entry.from) return 0;
    const entryStartHour = Math.floor(
      fromISO(entry.from)
        .diff(fromISO(options.date ?? entry.date))
        .as('hours')
    );
    return entryStartHour ?? 0;
  });
  const totalHours = Math.max(max(entryStartHours) ?? 0, 24) + 1;
  const { fromDateTime, toDateTime, date, isToday, hoursList } = timelineMetaForDate(options.date, {
    timeFormat,
    totalHours,
  });
  const scrollToPxFromTop = getTimelineScrollToPx({
    fromDateTime,
    hourDraftFrom,
    pixelsPerMinute,
  });

  return {
    fromDateTime,
    toDateTime,
    totalHours,
    totalMemories: entries.length,
    scrollToPxFromTop,
    date,
    isToday,
    hoursList,
  };
}

export interface TimelineMetaForDate {
  fromDateTime: DateTime<true>;
  toDateTime: DateTime<true>;
  date: string;
  isToday: boolean;
  hoursList: MetaHourList;
}
export function timelineMetaForDate(
  date: string | null,
  options: {
    totalHours: number;
    timeFormat: UserTimeFormat;
  } = {
    totalHours: 25,
    timeFormat: 24,
  }
): TimelineMetaForDate {
  const today = DateTime.now();
  const totalHours = options.totalHours;
  const fromDateTime = fromISO(date!).startOf('day');
  const toDateTime = fromISO(date!).plus({ hours: totalHours });
  const showLabelEveryNth = 5;
  const showLabelFactor = Math.ceil((percentOf(totalHours, 24) / 100) * showLabelEveryNth);

  const hoursList = range(totalHours + 1).map(hour => {
    return {
      hour: hour,
      label: formatTime(fromDateTime.plus({ hours: hour }).toISO(), options.timeFormat),
      showLabel: hour % showLabelFactor === 0,
    };
  });

  return {
    fromDateTime,
    toDateTime,
    date: fromDateTime.toISODate(),
    isToday: today.hasSame(fromDateTime, 'day'),
    hoursList,
  };
}

export const extractAttributesFromEntry = (attributes: EntryAttributes) => {
  if (!attributes) {
    return {};
  }
  return attributes.reduce((prev: Record<string, string>, current) => {
    prev[current.name] = current.value;
    return prev;
  }, {});
};

// This should really be more accessible in the API response
export const extractUrlFromEntry = (entry: Entry) => {
  const urlAttr = entry.extra_attributes.find(attr => {
    return attr.name === 'detail' && isUrl(attr.value);
  });
  return urlAttr ? urlAttr.value : null;
};

/**
 Build a lookup object to find the most relevant projects based on entry_id
 */
export const extractProjectPredictionOnEntryIdFromSuggestedEntries = (
  groups: Array<SuggestedEntry> = []
) => {
  return groups.reduce((prev, group) => {
    interface Prediction {
      project_id: number | null;
      accuracy: number;
    }
    const entryIdsToProjectMap: {
      [id: number]: Prediction & { id: number };
    } = {};

    group.entry_ids.forEach(entryId => {
      // Find the most accurate project to predict this memory to:
      const relevantProject = group.projects.reduce(
        (pPrev: Prediction, project) => {
          if (project.accuracy > pPrev.accuracy) {
            return {
              project_id: project.project_id,
              accuracy: project.accuracy,
            };
          }
          return pPrev;
        },
        { project_id: null, accuracy: 0 }
      );

      entryIdsToProjectMap[entryId] = {
        id: entryId,
        ...relevantProject,
      };
    });

    return {
      ...prev,
      ...entryIdsToProjectMap,
    };
  }, {});
};

type PredictionMap = Record<number, { id: number; entry_ids: Array<number> }>;
/**
 Build a lookup object to find the entry_ids predicted to a project_id
 */
export const extractProjectPredictionsFromSuggestedEntries = (
  groups: Array<SuggestedEntry> = []
): PredictionMap => {
  const emptyProjectGroup = { entry_ids: [], accuracies: [] };

  return groups.reduce((prev: PredictionMap, group) => {
    const predictions = group.projects.reduce((existing: PredictionMap, prediction) => {
      const { project_id } = prediction;
      const hasExistingProjectGroup = !!prev[project_id];
      const existingProjectGroup = hasExistingProjectGroup ? prev[project_id] : emptyProjectGroup;
      // const accuracies = [...existingProjectGroup.accuracies, { count, accuracy }];
      existing[prediction.project_id] = {
        id: prediction.project_id,
        // accuracies,
        // accuracy, // THIS IS NOT ACCURATE!!!!! ... only used for prototyping quickly
        entry_ids: [...existingProjectGroup.entry_ids, ...group.entry_ids],
      };
      return existing;
    }, {});

    return {
      ...prev,
      ...predictions,
    };
  }, {});
};

// Two almost identical functions below, done this way as I had real trouble getting
// Flow to work properly when only MemoryGroups have .isPartlyChecked
// TODO: This should totally be tested, but waiting for a final call on functionality.
interface FilterBasedOnDisplayRulesOptions {
  displayRules: DisplayRules;
}

/**
 * Removes any Memories that doesn't pass the display rules
 */
export const filterMemoriesByDisplayRule = <T extends Memory = Memory>(
  memories: Array<T> = [],
  { displayRules }: FilterBasedOnDisplayRulesOptions
): Array<T> => {
  return memories.filter(memory => {
    if (memory.isChecked) {
      return true;
    }
    if (!displayRules.showSpam && memory.isSpam) {
      return false;
    }
    if (displayRules.showSpam && memory.isSpam) {
      return true;
    }
    if (displayRules.showLogged && memory.isLogged) {
      return true;
    }
    // Predicted are considered logged in current logic ref https://app.clubhouse.io/timelyapp/story/9660
    // but I suspect we might split this out into separate filters
    if (displayRules.showUnlogged && !memory.isLogged && !memory.isPredicted) {
      return true;
    }
    if (displayRules.showPredicted && memory.isPredicted) {
      return true;
    }
    return false;
  });
};

export function memorySidebarDateReference(referenceDate: string, memoryDate: string) {
  const fallbackFormat = "'Memories from' LLL d";

  if (isSameDay(referenceDate, memoryDate)) {
    return "Today's Memories";
  }

  const dtMemoryDate = fromISO(memoryDate);
  const dtRefrenceDate = fromISO(referenceDate);

  if (referenceDate > memoryDate && dtRefrenceDate.diff(dtMemoryDate, 'days').days === 1) {
    return "Yesterday's Memories";
  }

  return dtMemoryDate.toFormat(fallbackFormat);
}

export function memorySidebarIsVisible(params: {
  userId: number;
  draftUserId: number;
  multiUserMode: boolean;
  properties: UserProperties;
  hasEnabledTic?: boolean;
}) {
  return (
    params.userId === params.draftUserId &&
    !params.multiUserMode &&
    (!!params.properties[SHOW_HOUR_EDITOR_ATTACHMENTS] || !!params.hasEnabledTic)
  );
}

export function enrichRangesForOverlapping(ranges: Array<Range>): Array<Range> {
  const enriched: Array<Range> = [];
  const sorted = [...ranges].sort(
    (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()
  );

  placeRangesIntoLanes(sorted).forEach((lane, laneIndex) => {
    lane.forEach(range =>
      enriched.push({
        ...range.range,
        laneIndex,
      })
    );
  });

  return enriched;
}

interface RangeAndLayoutData {
  range: Range;
  offset: number;
  length: number;
}

function placeRangesIntoLanes(ranges: Array<Range>): Array<Array<RangeAndLayoutData>> {
  const lanes: Array<Array<RangeAndLayoutData>> = [[]];

  ranges.forEach(range => {
    const fromSeconds = +Date.parse(range.from) / 1000;
    const toSeconds = +Date.parse(range.to) / 1000;
    const rangeWithLayout: RangeAndLayoutData = {
      range,
      offset: fromSeconds,
      length: toSeconds - fromSeconds,
    };

    lanes.some((lane, i) => {
      const isLastLane = i === lanes.length - 1;
      const hasOverlappingRange = lane.some(existingRange => {
        return rangesOverlap(rangeWithLayout, existingRange);
      });

      if (!hasOverlappingRange) {
        lane.push(rangeWithLayout);
        return true;
      } else if (hasOverlappingRange && isLastLane) {
        const newLane = [rangeWithLayout];
        lanes.push(newLane);
        return true;
      }

      return false;
    });
  });

  return lanes;
}

const rangesOverlap = (
  e1: { offset: number; length: number },
  e2: { offset: number; length: number }
): boolean => {
  if (e2.offset <= e1.offset && e1.offset < e2.offset + e2.length) {
    return true;
  }

  if (e1.offset <= e2.offset && e2.offset < e1.offset + e1.length) {
    return true;
  }

  return false;
};

export function getHourTimestampWidth(timelineStyle?: TimelineStyleTypes): number {
  return timelineStyle === TimelineStyleTypes.timeEntriesOnTimeline
    ? TIME_ENTRY_WIDTH_PX
    : TIMESPAN_WIDTH_PX;
}

export function getTotalHourTimestampWidth(ranges: Array<Range>, rangeWidth: number): number {
  if (ranges.length > 0) {
    const laneIndexes = ranges.map(range => range.laneIndex || 0);
    const numberOfLanes = Math.max(...laneIndexes) + 1;
    return TIMESPAN_SPACING_PX + numberOfLanes * (rangeWidth + TIMESPAN_SPACING_PX);
  }

  return 0;
}

export const createMemoryGroupRef = (entryIds: Array<number>, title: string) =>
  slugify(`${entryIds.join('-')} ${title}}`);
